import React, { useState, useEffect, useMemo } from 'react';
import { Building2, Search, ChevronUp, ChevronDown, ExternalLink, RefreshCw, Trash2, AlertTriangle, Download } from 'lucide-react';
import { downloadCSV } from '../utils/csvExport';
import { supabase } from '../supabase';
import { formatDate } from '../utils/commissionCalc';

export default function Companies({ navigateToCompany }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load all data without any RLS restrictions (service role key)
      // Using .range(0, 9999) to ensure we get all records (bypasses default 1000 limit)
      const [companiesRes, userCompaniesRes, usersRes] = await Promise.all([
        supabase
          .from('companies')
          .select('*', { count: 'exact' })
          .range(0, 9999)
          .order('created_at', { ascending: false }),
        supabase
          .from('user_companies')
          .select('*')
          .range(0, 9999),
        supabase
          .from('user_profiles')
          .select('*')
          .range(0, 9999)
      ]);

      if (companiesRes.error) throw companiesRes.error;
      if (userCompaniesRes.error) throw userCompaniesRes.error;
      if (usersRes.error) throw usersRes.error;

      const companiesData = companiesRes.data || [];
      const userCompaniesData = userCompaniesRes.data || [];
      const usersData = usersRes.data || [];

      console.log(`Loaded: ${companiesData.length} companies, ${userCompaniesData.length} user_companies, ${usersData.length} users`);

      // Build user lookup
      const userMap = {};
      usersData.forEach(u => { userMap[u.id] = u; });

      // Build owner map and team size map
      const ownerMap = {};
      const teamSizeMap = {};
      userCompaniesData.forEach(uc => {
        teamSizeMap[uc.company_id] = (teamSizeMap[uc.company_id] || 0) + 1;
        if (uc.role === 'owner') {
          ownerMap[uc.company_id] = userMap[uc.user_id];
        }
      });

      const enriched = companiesData.map(c => ({
        ...c,
        owner: ownerMap[c.id] || null,
        teamSize: teamSizeMap[c.id] || 0
      }));

      setCompanies(enriched);
    } catch (err) {
      console.error('Error loading companies:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    let result = companies.filter(c => {
      const matchesSearch = searchTerm === '' ||
        (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.owner?.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.owner?.full_name || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || c.subscription_status === filterStatus;
      return matchesSearch && matchesStatus;
    });

    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'owner':
          aVal = (a.owner?.full_name || a.owner?.email || '').toLowerCase();
          bVal = (b.owner?.full_name || b.owner?.email || '').toLowerCase();
          break;
        case 'subscription_status':
          aVal = a.subscription_status || '';
          bVal = b.subscription_status || '';
          break;
        case 'tier':
          aVal = a.tier || '';
          bVal = b.tier || '';
          break;
        case 'teamSize':
          aVal = a.teamSize;
          bVal = b.teamSize;
          break;
        case 'created_at':
        default:
          aVal = a.created_at || '';
          bVal = b.created_at || '';
          break;
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [companies, searchTerm, filterStatus, sortField, sortDir]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ChevronUp size={14} style={{ marginLeft: '0.25rem' }} />
      : <ChevronDown size={14} style={{ marginLeft: '0.25rem' }} />;
  };

  const statusBadge = (status) => {
    const colors = {
      active: { bg: '#4ecca320', text: '#4ecca3' },
      trialing: { bg: '#f7931e20', text: '#f7931e' },
      canceled: { bg: '#e74c3c20', text: '#e74c3c' },
      past_due: { bg: '#e74c3c20', text: '#e74c3c' },
      incomplete: { bg: '#88888820', text: '#888' }
    };
    const c = colors[status] || colors.incomplete;
    return (
      <span style={{
        padding: '0.25rem 0.5rem',
        background: c.bg,
        color: c.text,
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: '600',
        textTransform: 'capitalize'
      }}>
        {status || 'unknown'}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading companies...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <div style={{ color: '#e74c3c', marginBottom: '1rem' }}>
          <AlertTriangle size={32} style={{ marginBottom: '0.5rem' }} />
          <div>Failed to load companies</div>
        </div>
        <div style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem' }}>{error}</div>
        <button
          onClick={loadData}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#ff6b35',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  const thStyle = {
    textAlign: 'left',
    padding: '0.875rem 1rem',
    color: '#888',
    fontWeight: '500',
    fontSize: '0.85rem',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap'
  };

  return (
    <div style={{ padding: '2rem 3rem', boxSizing: 'border-box', maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '0.5rem'
          }}>
            Companies
          </h1>
          <p style={{ color: '#888' }}>
            All Work Van companies across the platform ({companies.length} total)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={() => downloadCSV(filtered, [
              { key: 'name', label: 'Name' },
              { key: 'business_type', label: 'Business Type' },
              { key: 'subscription_status', label: 'Subscription Status' },
              { key: 'tier', label: 'Tier' },
              { key: 'trial_end', label: 'Trial End', format: (v) => v ? new Date(v).toLocaleDateString() : '' },
              { key: 'created_at', label: 'Created At', format: (v) => v ? new Date(v).toLocaleDateString() : '' }
            ], 'companies-export.csv')}
            style={{
              padding: '0.5rem 1rem',
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: '#e0e0e0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.85rem'
            }}
          >
            <Download size={16} />
            Export CSV
          </button>
          <button
            onClick={loadData}
            style={{
              padding: '0.5rem 1rem',
              background: '#2a2a2a',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              color: '#888',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: '1 1 300px', position: 'relative' }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#888'
          }} />
          <input
            type="text"
            placeholder="Search by company name, owner email, or owner name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.75rem',
              background: '#1a1a1a',
              border: '1px solid #2a2a2a',
              borderRadius: '8px',
              color: '#e0e0e0'
            }}
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '0.75rem 1rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            minWidth: '160px'
          }}
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="canceled">Canceled</option>
          <option value="past_due">Past Due</option>
        </select>
      </div>

      {/* Count */}
      <div style={{
        marginBottom: '1rem',
        fontSize: '0.85rem',
        color: '#888'
      }}>
        Showing {filtered.length} of {companies.length} companies
      </div>

      {/* Table */}
      <div style={{
        background: '#1a1a1a',
        borderRadius: '12px',
        border: '1px solid #2a2a2a',
        overflowX: 'auto'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2a2a2a' }}>
              <th style={thStyle} onClick={() => handleSort('name')}>
                Name <SortIcon field="name" />
              </th>
              <th style={thStyle} onClick={() => handleSort('owner')}>
                Owner <SortIcon field="owner" />
              </th>
              <th style={thStyle} onClick={() => handleSort('subscription_status')}>
                Status <SortIcon field="subscription_status" />
              </th>
              <th style={thStyle} onClick={() => handleSort('tier')}>
                Plan <SortIcon field="tier" />
              </th>
              <th style={thStyle} onClick={() => handleSort('created_at')}>
                Created <SortIcon field="created_at" />
              </th>
              <th style={thStyle} onClick={() => handleSort('teamSize')}>
                Team <SortIcon field="teamSize" />
              </th>
              <th style={{ ...thStyle, cursor: 'default', textAlign: 'center' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
                  No companies found
                </td>
              </tr>
            ) : (
              filtered.map(company => (
                <tr
                  key={company.id}
                  onClick={() => navigateToCompany(company.id)}
                  style={{
                    borderBottom: '1px solid #2a2a2a',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#242424'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontWeight: '600', color: '#e0e0e0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {company.name || 'Unnamed Company'}
                      {company.is_demo && (
                        <span style={{
                          background: '#ff6b35',
                          color: '#fff',
                          padding: '0.1rem 0.4rem',
                          borderRadius: '4px',
                          fontSize: '0.6rem',
                          fontWeight: 'bold',
                          letterSpacing: '0.5px'
                        }}>DEMO</span>
                      )}
                    </div>
                    <div style={{ color: '#666', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                      {company.id.substring(0, 8)}...
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {company.owner ? (
                      <div>
                        <div style={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                          {company.owner.full_name || 'Unnamed'}
                        </div>
                        <div style={{ color: '#666', fontSize: '0.8rem' }}>
                          {company.owner.email}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#666' }}>No owner</span>
                    )}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {statusBadge(company.subscription_status)}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      background: '#3498db20',
                      color: '#3498db',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {company.tier || '-'}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', color: '#888', fontSize: '0.9rem' }}>
                    {formatDate(company.created_at)}
                  </td>
                  <td style={{ padding: '1rem', color: '#e0e0e0', textAlign: 'center' }}>
                    {company.teamSize}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToCompany(company.id);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.4rem 0.75rem',
                        background: '#2a2a2a',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#888',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#3a3a3a';
                        e.currentTarget.style.color = '#e0e0e0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#2a2a2a';
                        e.currentTarget.style.color = '#888';
                      }}
                    >
                      <ExternalLink size={14} />
                      Manage
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
